import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";

import { ComplexCombobox } from "../../../shared";
import {
  ADMIN_STRUCTURES_FILTER_KEYS,
  useAdminStructuresUrlQuery,
} from "../api/structures";
import { AdminStructuresTab } from "../components/organisms";

const AdminStructuresPage = () => {
  const { translate } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();

  const complexId = searchParams.get("complexId");

  const { query, setFilters, resetFilters, currentFilters } =
    useAdminStructuresUrlQuery(complexId);

  const handleSelectComplex = useCallback(
    (value: string | "") => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value) {
          next.delete("complexId");
        } else {
          next.set("complexId", value);
        }
        next.delete("page");
        for (const key of ADMIN_STRUCTURES_FILTER_KEYS) {
          next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">
          {translate("admin.structures.page.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("admin.structures.page.subtitle")}
        </p>
      </header>

      <div className="w-full max-w-md">
        <ComplexCombobox
          label={translate("admin.structures.complexSelector.label")}
          placeholder={translate(
            "admin.structures.complexSelector.placeholder",
          )}
          clearable
          value={complexId ?? ""}
          onChange={handleSelectComplex}
        />
      </div>

      {complexId ? (
        <LoadingBlurDiv isLoading={query.isFetching}>
          <AdminStructuresTab
            complexId={complexId}
            structures={query.data?.data || []}
            paginatedMeta={query.data?.meta}
            isLoading={query.isFetching}
            onFilter={setFilters}
            onClearFilter={resetFilters}
            initialFilters={currentFilters}
          />
        </LoadingBlurDiv>
      ) : (
        <div className="flex items-center justify-center rounded-md border border-dashed border-muted-foreground/30 p-12 text-center text-sm text-muted-foreground">
          {translate("admin.structures.complexSelector.empty")}
        </div>
      )}
    </div>
  );
};

export default AdminStructuresPage;
